import { ConfluencePageAri } from '@atlassian/ari';

import { SOURCE_EXCERPT_LIMIT } from '../../utils/extractSmartLinkSourceExcerptsFromAdf.ts';
import type { EnhancedSmartLink } from '../../utils/smartLinkTypes.ts';
import type {
	AtAGlanceCollection,
	AtAGlanceEntityContext,
	AtAGlanceLinkedEntity,
	AtAGlanceRelationship,
	AtAGlanceStructuralAncestor,
} from '../atAGlanceTypes.ts';

import { bound, toIsoInstant, AT_A_GLANCE_INPUT_LIMITS } from './atAGlanceInputFields.ts';

const {
	TITLE_LIMIT,
	ENTITY_SUMMARY_LIMIT,
	ENTITY_STATUS_LIMIT,
	PRODUCT_LIMIT,
	RESOURCE_TYPE_LIMIT,
} = AT_A_GLANCE_INPUT_LIMITS;

type LinkBackedCollection = Exclude<AtAGlanceCollection, 'ancestors'>;

const LINK_RELATIONSHIPS = {
	appears_in: {
		type: 'content_referenced_entity',
		direction: 'incoming',
	},
	related_content: {
		type: 'confluence_content_has_related_confluence_content',
		direction: 'outgoing',
	},
	links_to: {
		type: 'page_contains_smart_link',
		direction: 'outgoing',
	},
} satisfies Record<LinkBackedCollection, Pick<AtAGlanceRelationship, 'type' | 'direction'>>;

const relationshipFor = (
	collection: LinkBackedCollection,
	url: string,
	sourceExcerpts: ReadonlyMap<string, string>,
): AtAGlanceRelationship => {
	const { type, direction } = LINK_RELATIONSHIPS[collection];
	return {
		collection,
		type,
		direction,
		source_excerpt:
			collection === 'links_to'
				? (bound(sourceExcerpts.get(url), SOURCE_EXCERPT_LIMIT) ?? null)
				: null,
	};
};

const entityContextFor = (link: EnhancedSmartLink): AtAGlanceEntityContext | undefined => {
	const summary = bound(link.summary, ENTITY_SUMMARY_LIMIT);
	const status =
		bound(link.taskStatus, ENTITY_STATUS_LIMIT) ?? bound(link.state, ENTITY_STATUS_LIMIT);
	const updatedAt = toIsoInstant(link.updatedAt);
	return summary || status || updatedAt
		? {
				...(summary ? { summary } : {}),
				...(status ? { status } : {}),
				...(updatedAt ? { updated_at: updatedAt } : {}),
			}
		: undefined;
};

const toEntity = (
	link: EnhancedSmartLink,
	collection: LinkBackedCollection,
	sourceExcerpts: ReadonlyMap<string, string>,
): AtAGlanceLinkedEntity | null => {
	const title = bound(link.title, TITLE_LIMIT);
	if (link.access !== 'granted' || !link.ari || !title) {
		return null;
	}

	const entityContext = entityContextFor(link);
	const product = bound(link.product, PRODUCT_LIMIT);
	const resourceType = bound(link.resourceType, RESOURCE_TYPE_LIMIT);

	return {
		ari: link.ari,
		title,
		...(product ? { product } : {}),
		...(resourceType ? { resource_type: resourceType } : {}),
		relationships: [relationshipFor(collection, link.url, sourceExcerpts)],
		...(entityContext ? { entity_context: entityContext } : {}),
	};
};

export const collectEntities = (
	links: readonly EnhancedSmartLink[],
	collection: LinkBackedCollection,
	sourceExcerpts: ReadonlyMap<string, string>,
): AtAGlanceLinkedEntity[] => {
	const entities: AtAGlanceLinkedEntity[] = [];
	for (const link of links) {
		const entity = toEntity(link, collection, sourceExcerpts);
		if (entity) {
			entities.push(entity);
		}
	}
	return entities;
};

export const structuralParentEntityFor = (
	cloudId: string,
	ancestors: readonly AtAGlanceStructuralAncestor[] | null | undefined,
): AtAGlanceLinkedEntity | null => {
	if (!ancestors) {
		return null;
	}

	// Confluence ancestors are root-first. Select one nearest page ancestor so
	// hierarchy evidence stays useful and bounded even below folders/databases.
	for (let index = ancestors.length - 1; index >= 0; index--) {
		const ancestor = ancestors[index];
		const id = ancestor?.id?.trim();
		const title = bound(ancestor?.title ?? undefined, TITLE_LIMIT);
		if (!id || !title || ancestor?.type?.toLowerCase() !== 'page') {
			continue;
		}

		return {
			ari: ConfluencePageAri.create({ siteId: cloudId, pageId: id }).toString(),
			title,
			product: 'confluence',
			resource_type: 'page',
			relationships: [
				{
					collection: 'ancestors',
					type: 'confluence_page_has_child_confluence_page',
					direction: 'incoming',
					source_excerpt: null,
				},
			],
		};
	}

	return null;
};
